import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { Shield, HeartPulse, TrendingUp, ArrowUpRight, Check } from "lucide-react";
import useDocTitle from "../lib/useDocTitle";
import ComplianceDisclaimer from "../components/ComplianceDisclaimer";

function StatusBadge({ tone, children }) {
  const tones = {
    live: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    dev: "bg-amber-50 text-amber-800 ring-amber-200",
  };
  const dot = {
    live: "bg-emerald-500",
    dev: "bg-amber-500",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ${tones[tone]}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot[tone]}`} />
      {children}
    </span>
  );
}

export default function Products() {
  useDocTitle(
    "Products · Lulo Software",
    "Three SaaS products serving distinct verticals: e-commerce operational tooling, healthcare safety information, and micro-SaaS opportunity research.",
    "Appeal Architect, BlendSafe, PrimePillar, e-commerce operational tooling, skincare medication safety, micro-SaaS opportunity scanner",
    "/products",
  );

  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hash]);

  return (
    <div data-testid="products-page" className="bg-white">
      <section className="border-b border-slate-200">
        <div className="lulo-container py-16 lg:py-20">
          <p className="text-xs font-semibold uppercase tracking-wider text-[#2563EB]">Product portfolio</p>
          <h1 className="mt-2 text-3xl sm:text-5xl font-semibold tracking-tight text-[#0A2540]">
            Our Product Portfolio
          </h1>
          <p className="mt-4 max-w-2xl text-slate-600 leading-7">
            Three SaaS products serving distinct verticals — e-commerce
            operational tooling, healthcare safety information, and micro-SaaS
            opportunity research.
          </p>
        </div>
      </section>

      {/* Appeal Architect */}
      <section id="appeal-architect" data-testid="product-appeal-architect" className="border-b border-slate-200">
        <div className="lulo-container py-16 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-[#0A2540]/5 text-[#0A2540]">
                  <Shield size={22} />
                </div>
                <StatusBadge tone="live">Available</StatusBadge>
              </div>
              <h2 className="mt-5 text-2xl font-semibold text-[#0A2540]">Appeal Architect</h2>
              <p className="mt-2 text-sm text-slate-600">Compliance and operational risk diagnostic tooling</p>
              <div className="mt-6 rounded-lg border border-slate-200 bg-[#F9FAFB] p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pricing</p>
                <p className="mt-2 text-sm text-slate-700 leading-6">
                  One-time licenses from <span className="font-semibold text-[#0A2540]">$49</span>. Monthly
                  compliance monitoring from <span className="font-semibold text-[#0A2540]">$29/month</span>.
                </p>
                <Link
                  to="/pricing"
                  className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#2563EB] hover:text-[#1d4ed8]"
                  data-testid="product-appeal-pricing-link"
                >
                  View full pricing <ArrowUpRight size={14} />
                </Link>
              </div>
              <a
                href="https://appealarchitect.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-appeal-visit-cta"
                className="mt-5 inline-flex items-center justify-center rounded-md bg-[#0A2540] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#081c30]"
              >
                Visit appealarchitect.com <ArrowUpRight size={16} className="ml-1.5" />
              </a>
            </div>

            <div className="lg:col-span-8 space-y-5 lulo-prose">
              <p>
                Appeal Architect is a structured operational workflow platform
                that helps merchants organize documentation, understand platform
                policy requirements, and coordinate internal review processes
                across major marketplaces and payment platforms.
              </p>
              <p>
                The system combines deterministic workflow mapping, evidence
                organization, policy-reference guidance, and documentation
                readiness tools to support merchant operational teams.
              </p>
              <p>
                Appeal Architect does not access customer platform accounts,
                automate submissions, interact with platform systems, or warrant
                outcomes of any review process.
              </p>
              <p>
                All outputs are educational and operational in nature and should
                be independently reviewed by qualified professionals before
                submission or business use.
              </p>

              <div className="mt-2 rounded-xl border border-slate-200 bg-white p-6">
                <h3 className="text-sm font-semibold text-[#0A2540]">Key capabilities</h3>
                <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-700">
                  {[
                    "Documentation workflow library with 218 documented patterns",
                    "177 platform-specific policy-reference workflows",
                    "1,018 structured workflow steps",
                    "Documentation Readiness Review",
                    "Multi-platform coverage (18+ marketplaces)",
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={16} className="mt-0.5 text-[#2563EB]" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-2 rounded-xl border border-slate-200 bg-[#F9FAFB] p-5">
                <p className="text-sm text-slate-700 leading-6">
                  <strong className="text-slate-900">Informational tooling only.</strong>{" "}
                  Appeal Architect is operational software that helps merchant
                  teams organise documentation and reference platform policy
                  text. It does not access customer platform accounts, request
                  credentials, automate submissions, interact with third-party
                  marketplace or payment-platform systems, or warrant any
                  business or review outcome. Outputs are educational reference
                  material and should be independently reviewed by qualified
                  legal, compliance, or operational professionals before any
                  business use. Appeal Architect is independent operational
                  tooling and is not affiliated with, endorsed by, or
                  partnered with any third-party marketplace or payment
                  platform.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BlendSafe */}
      <section id="medskin" data-testid="product-medskin" className="border-b border-slate-200 bg-[#F9FAFB]">
        <div className="lulo-container py-16 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-[#0A2540]/5 text-[#0A2540]">
                  <HeartPulse size={22} />
                </div>
                <StatusBadge tone="live">Available</StatusBadge>
              </div>
              <h2 className="mt-5 text-2xl font-semibold text-[#0A2540]">BlendSafe</h2>
              <p className="mt-2 text-sm text-slate-600">
                Consumer reference tool for skincare-medication compatibility
              </p>
              <div className="mt-6 rounded-lg border border-slate-200 bg-white p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pricing</p>
                <p className="mt-2 text-sm text-slate-700 leading-6">
                  <span className="font-semibold text-[#0A2540]">Free</span> safety checks
                  with no account required. A premium tier is reserved for the first 100
                  Founding Members (early-access programme).
                </p>
                <Link
                  to="/pricing"
                  className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#2563EB] hover:text-[#1d4ed8]"
                  data-testid="product-medskin-pricing-link"
                >
                  View full pricing <ArrowUpRight size={14} />
                </Link>
              </div>
              <a
                href="https://www.blendsafe.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-medskin-visit"
                className="mt-5 inline-flex items-center justify-center rounded-md bg-[#0A2540] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#081c30]"
              >
                Visit blendsafe.com <ArrowUpRight size={16} className="ml-1.5" />
              </a>
            </div>

            <div className="lg:col-span-8 space-y-5 lulo-prose">
              <p>
                BlendSafe is a consumer reference tool that helps people on
                dermatology prescriptions check whether a specific skincare
                product or ingredient is compatible with their medication.
              </p>
              <p>
                The customer selects their prescription and a skincare product
                (by name, ingredient list, photo, or barcode). BlendSafe
                cross-references publicly available reference sources and
                returns a single, plain-language verdict — <strong>SAFE</strong>,{" "}
                <strong>CAUTION</strong>, or <strong>AVOID</strong> — along with
                a short explanation and the underlying citations.
              </p>
              <p>
                The reference dataset is built from FDA-published drug-label
                data (DailyMed and the FDA Orange Book), peer-reviewed
                dermatology literature, and INCI ingredient nomenclature.
                Drug-label content is refreshed against DailyMed daily. The
                evidence mix is approximately 60% peer-reviewed studies, 25%
                FDA labels, and 15% INCI naming.
              </p>
              <p>
                BlendSafe currently focuses on US-market dermatology
                prescriptions and consumer skincare products. Categories
                covered include acne and retinoids, rosacea, antibiotics,
                eczema and barrier care, photosensitivity, hormonal and PCOS,
                hyperpigmentation, anti-aging, pregnancy-safe routines, and
                sun protection.
              </p>

              <div className="mt-2 rounded-xl border border-slate-200 bg-white p-6">
                <h3 className="text-sm font-semibold text-[#0A2540]">Key capabilities</h3>
                <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-700">
                  {[
                    "Plain-language SAFE / CAUTION / AVOID verdict per check",
                    "545+ dermatology prescriptions indexed",
                    "14,000+ INCI ingredient names cross-referenced",
                    "320+ peer-reviewed dermatology studies cited",
                    "2,250+ documented interaction rules",
                    "Daily DailyMed drug-label sync",
                    "Search by product name, ingredient list, photo, or barcode",
                    "Full AM/PM routine analysis for stack conflicts",
                    "Category browser across 10 dermatology contexts",
                    "Evidence citations linked from every verdict",
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={16} className="mt-0.5 text-[#2563EB]" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-5">
                <p className="text-sm text-amber-900 leading-6">
                  <strong>Informational reference only.</strong> BlendSafe is a
                  general-purpose consumer reference tool. It is not a medical
                  device, does not diagnose, treat, or prescribe, and does not
                  replace the judgement of a qualified healthcare provider.
                  Users should always consult their prescribing physician or
                  dermatologist for individual medical decisions. BlendSafe
                  receives no compensation from product manufacturers and runs
                  no paid placements.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PrimePillar */}
      <section id="paintoprofit" data-testid="product-paintoprofit" className="border-b border-slate-200">
        <div className="lulo-container py-16 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="flex items-center gap-3">
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-md bg-[#0A2540]/5 text-[#0A2540]">
                  <TrendingUp size={22} />
                </div>
                <StatusBadge tone="live">Available</StatusBadge>
              </div>
              <h2 className="mt-5 text-2xl font-semibold text-[#0A2540]">PrimePillar</h2>
              <p className="mt-2 text-sm text-slate-600">
                Micro-SaaS opportunity scanner for indie founders and small-team SaaS builders
              </p>
              <div className="mt-6 rounded-lg border border-slate-200 bg-[#F9FAFB] p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pricing</p>
                <p className="mt-2 text-sm text-slate-700 leading-6">
                  <span className="font-semibold text-[#0A2540]">Free</span> tier with weekly
                  signals. Paid plans from{" "}
                  <span className="font-semibold text-[#0A2540]">$19/month</span> (Founder) and{" "}
                  <span className="font-semibold text-[#0A2540]">$49/month</span> (Pro).
                </p>
                <Link
                  to="/pricing"
                  className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#2563EB] hover:text-[#1d4ed8]"
                  data-testid="product-ptp-pricing-link"
                >
                  View full pricing <ArrowUpRight size={14} />
                </Link>
              </div>
              <a
                href="https://primepillar.io"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="product-ptp-visit"
                className="mt-5 inline-flex items-center justify-center rounded-md bg-[#0A2540] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#081c30]"
              >
                Visit primepillar.io <ArrowUpRight size={16} className="ml-1.5" />
              </a>
            </div>

            <div className="lg:col-span-8 space-y-5 lulo-prose">
              <p>
                PrimePillar is an automated opportunity-scanner that helps indie
                founders and small SaaS teams turn publicly available demand
                signals into structured, evidence-linked product briefs.
              </p>
              <p>
                The platform indexes signals from seven public sources — Reddit,
                Fiverr, Upwork, G2, ProductHunt, YouTube, and Google Trends — and
                refreshes its dataset four times per day at fixed UTC intervals
                (02:00, 06:00, 12:00, 18:00). Every signal is linked back to its
                original public post so customers can verify the underlying
                evidence themselves.
              </p>
              <p>
                Signals are organised by an internal Pain-to-Profit (P2P) score
                from 0 to 100, blended from public demand volume, observable
                competition, automation feasibility, and observable willingness
                to pay (e.g. paid Fiverr / Upwork engagements). When the same
                pain pattern appears on three or more independent public
                platforms, it is flagged as cross-source-validated.
              </p>
              <p>
                One-click brief generation is powered by Anthropic Claude. The
                generated briefs are operational planning documents — they
                summarise the public evidence, suggest a positioning angle,
                outline three candidate features with verbatim public-complaint
                quotes, and propose a go-to-market angle. Briefs export to PDF
                and CSV for use in the customer&apos;s own planning tools.
              </p>
              <p>
                PrimePillar operates only on publicly accessible content. It
                does not access gated platforms, request user credentials, scrape
                private data, or interact with any third-party platform on the
                customer&apos;s behalf.
              </p>

              <div className="mt-2 rounded-xl border border-slate-200 bg-white p-6">
                <h3 className="text-sm font-semibold text-[#0A2540]">Key capabilities</h3>
                <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-slate-700">
                  {[
                    "Seven-source public-signal index (Reddit, Fiverr, Upwork, G2, ProductHunt, YouTube, Google Trends)",
                    "4× daily scheduled refresh at 02 / 06 / 12 / 18 UTC",
                    "Deterministic 0–100 Pain-to-Profit (P2P) score",
                    "Cross-source validation flag (3+ platforms agree)",
                    "Quick-Win Quadrant: score × source-count matrix",
                    "7-day momentum trajectory + rising-signal flag",
                    "Niche personalisation across 17 broad categories",
                    "G2 Pain Index: B2B SaaS ranked by negative-review density",
                    "Claude-drafted product brief with PDF and CSV export",
                    "Persistent brief library for ongoing reference",
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check size={16} className="mt-0.5 text-[#2563EB]" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-2 rounded-xl border border-slate-200 bg-[#F9FAFB] p-5">
                <p className="text-sm text-slate-700 leading-6">
                  <strong className="text-slate-900">Informational tooling only.</strong>{" "}
                  PrimePillar outputs are operational research aids and do not
                  constitute investment, business, or legal advice. Customers
                  are responsible for independently validating any opportunity
                  before allocating capital or building product. No business
                  outcome is warranted, implied, or guaranteed.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <ComplianceDisclaimer />
    </div>
  );
}
